import { useCallback, useEffect } from "react";
import type { HMSNotification } from "@100mslive/react-sdk";
import {
  HMSNotificationTypes,
  useHMSNotifications,
} from "@100mslive/react-sdk";
import type {
  ParticipantMetadata,
  ProviderNotificationsListenerOptions,
} from "../types";
import { METADATA_TYPE, SYSTEM_MESSAGES, SYSTEM_TYPE } from "./constants";
import { useHMSCamera } from "./useHMSCamera";
import { useHMSLocalMetadata } from "./useHMSLocalMetadata";
import { useHMSMicrophone } from "./useHMSMicrophone";

interface MessageData {
  enabled?: boolean;
  message?: string;
  reason?: string;
  track?: {
    type: string;
  };
  type?: string;
}

// 100ms notifications handling
export const useHMSNotificationsListener = ({
  onRoomUpdate,
  onEndStream,
}: ProviderNotificationsListenerOptions) => {
  const hmsNotification = useHMSNotifications();
  const { setMetadata } = useHMSLocalMetadata();
  const { toggleMicrophone, isMicrophoneEnabled } = useHMSMicrophone();
  const { toggleCamera, isCameraEnabled } = useHMSCamera();

  const onChangeTrackStateRequest = useCallback(
    (data: MessageData) => {
      const { track, enabled: isEnabled } = data;
      if (isEnabled !== false) {
        // do not allow to remotely enable the track
        return;
      }
      if (track?.type === "audio" && isMicrophoneEnabled) {
        void toggleMicrophone();
      }
      if (track?.type === "video" && isCameraEnabled) {
        void toggleCamera();
      }
    },
    [isMicrophoneEnabled, isCameraEnabled, toggleMicrophone, toggleCamera],
  );

  useEffect(() => {
    if (!hmsNotification) {
      return;
    }

    const notification = hmsNotification as HMSNotification;
    const { type } = notification;
    const data = notification.data as MessageData;
    const { type: messageType, message, reason } = data || {};

    switch (type) {
      case HMSNotificationTypes.NEW_MESSAGE:
        if (
          messageType === SYSTEM_TYPE &&
          message === SYSTEM_MESSAGES.UPDATE_ROOM
        ) {
          onRoomUpdate?.();
        } else if (messageType === METADATA_TYPE) {
          const newMetadata = message
            ? (JSON.parse(message) as ParticipantMetadata)
            : null;
          if (newMetadata && typeof newMetadata === "object") {
            setMetadata(newMetadata);
          }
        }
        return;
      case HMSNotificationTypes.ROOM_ENDED:
        onEndStream?.(reason);
        return;
      case HMSNotificationTypes.CHANGE_MULTI_TRACK_STATE_REQUEST:
      case HMSNotificationTypes.CHANGE_TRACK_STATE_REQUEST: {
        // Note: Contents of data:
        // const { requestedBy, track, enabled } = data;
        onChangeTrackStateRequest(data);
        break;
      }
      default:
        // Ignore other notifications for now
        return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- We only want to run when hmsNotification changes
  }, [hmsNotification, onChangeTrackStateRequest]);
};
